import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import api from 'state/axios';
import { format, parse, isValid } from 'date-fns';
import toast from 'react-hot-toast';
import { useTheme, useMediaQuery, Box,   
  CircularProgress, Typography, Tooltip, IconButton, 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import UpdateIcon from '@mui/icons-material/Update';
import SyncIcon from '@mui/icons-material/Sync';
import SaveIcon from '@mui/icons-material/Save';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const AirportPairs = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "AirportPairs", "create", false);
  const canEdit = hasPermission(user?.permissions, "AirportPairs", "update", false);
  const canDelete = hasPermission(user?.permissions, "AirportPairs", "delete", false);

  const { startDate, endDate } = useDateRangeContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); 

  const [airportPairs, setAirportPairs] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

    
  const fetchAirportPairs = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const url = '/airportpairs';
      const response = await api.get(url);
      setAirportPairs(response.data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAirportPairs();
  }, [fetchAirportPairs]);
  
  const cleanDate = (dateString) => {
    try {
      if (!dateString) return null;
    
      const cleaned = dateString.replace(/[\n\r\t]/g, "").trim();

      // Nếu là ISO string thì dùng new Date()
      const isoDate = new Date(cleaned);
      if (isValid(isoDate)) {
        return format(isoDate, "yyyy-MM-dd");
      }

      // Nếu không phải ISO, thử theo các format khác
      const formatsToTry = [
        "yyyy-MM-dd",
        "yyyy/MM/dd",
        "dd/MM/yyyy",
        "MM/dd/yyyy",
        "dd-MM-yyyy",
      ];

      for (const fmt of formatsToTry) {
        const parsed = parse(cleaned, fmt, new Date());
        if (isValid(parsed)) {
          return format(parsed, "yyyy-MM-dd");
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  const parseExcelData = (data) => {
    try {
      const rows = data
        .trim()
        .split("\n")
        .map((row, index) => {
          const cells = row.split("\t");
          // if (cells.length < 6 || cells.length > 9) {
          //   throw new Error(`Row ${index + 1} is invalid or incomplete.`);
          // }

          return {
            _id: index + 1, 
            sector: cells[0],
            routeName: cells[1],
            blockHours: cells[2],
            distance: cells[3],
            startDate: cleanDate(cells[4]),
            endDate: cleanDate(cells[5]),
            description: cells[6],
          };
        });

      return rows;
    } catch (error) {
      toast.success(error?.message || "Failed to parse the clipboard!");
      return null;
    }
  };

  const handlePaste = (event) => {
    try
    {
      event.preventDefault();
      const clipboardData = event.clipboardData.getData("text");
      const parsedRows = parseExcelData(clipboardData);
  
      if (parsedRows) {
        setAirportPairs((prevRows) => [...prevRows, ...parsedRows]);
        setRowSelectionModel({ type: 'include', ids: new Set() });         
      }
    } catch (error) {
      toast.error("Failed to parse the clipboard!")
    }        
  };      
      
  const handleRowUpdate = (newRow) => {
    const updatedRows = airportPairs.map((row) =>
      row._id === newRow._id ? { ...row, ...newRow } : row
    );
    setAirportPairs(updatedRows);
    return newRow;
  };

  const handleDeleteRows = useCallback(() => {
    const selectedIds = getSelectedIds(rowSelectionModel, airportPairs);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setAirportPairs((prev) => prev.filter((row) => !selectedIds.includes(row._id)));    
    setRowSelectionModel({ type: 'include', ids: new Set() }); 
    toast.success('Selected rows removed successfully.');
  }, [rowSelectionModel, airportPairs]);
  
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Delete') {
        event.preventDefault(); 
        handleDeleteRows();
      }
    }, [handleDeleteRows]
  );

  const handleSave = useCallback(async () => {
    if (airportPairs.length === 0) {
      toast.error("No data to save!");
      return;
    }

    try {
      const url = '/airportpairs/save';
      const payload = airportPairs.map(row => {
        if (!row.sector) return null;
        return {
          sector: row.sector?.trim(),
          routeName: row.routeName?.trim() || "",
          blockHours: Number(row.blockHours) || 0,
          distance: Number(row.distance) || 0,
          startDate: cleanDate(row.startDate) || undefined,
          endDate: cleanDate(row.endDate) || undefined,
          description: row.description?.trim() || "",
          isActive: row.isActive ?? true,
        };
      })
      .filter(Boolean);

      if (payload.length === 0) {
        toast.error("No valid rows to save!");
        return;
      }
     
      const BATCH_SIZE = 100;
      const batchArray = (array, size) =>
        Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
          array.slice(i * size, i * size + size)
        );

      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      const batches = batchArray(payload, BATCH_SIZE);

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        try {
          const res = await api.post(url, batch);

          if (res.data.success) {
            toast.success(
              `Batch ${i + 1}/${batches.length} saved successfully (${batch.length} records).`
            );
          } else {
            toast.error(
              `Batch ${i + 1}/${batches.length} failed: ${res.data?.message || "Unknown error"}`
            );
          }
        } catch (err) {
          toast.error(
            `Batch ${i + 1}/${batches.length} failed: ${err.message}`
          );
        }

        // Chờ 5 giây trước khi gửi batch tiếp theo (chỉnh ms tùy nhu cầu)
        if (i < batches.length - 1) {
          await delay(2000);
        }
      }

      setAirportPairs([]);
    } catch (error) {
      toast.error(error.message || "Failed to save airport pairs!");
    }
  }, [airportPairs]);
  
  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    if (rowSelectionModel.ids.size === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {     
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        const url = `/airportpairs/delete/${idToDelete}`;        
        await api.delete(url);

        setAirportPairs(airportPairs.filter((item) => item._id !== idToDelete));
        toast.success('Airport pair deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, airportPairs);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/airportpairs/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setAirportPairs(airportPairs.filter((item) => !selectedIds.includes(item._id)));        
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete airport pairs!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    airportPairs
  ]);

  const handleUpdate = async () => {
    try {    
      setError(null);
      setLoading(true);
  
      const url = '/airportpairs/updateblockhours';
      const payload = { startDate, endDate };
      const res = await api.post(url, payload);

      toast.success(res.data?.message || `Airport pairs updated successfully.`);
      fetchAirportPairs();      
    } catch (error) {
      setError(error);
      toast.error(error.message || "Failed to update airport pairs!" );
    } finally {
      setLoading(false);
    }
  };
  
  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      ...isMobile ? { width: 60 } : { flex: 1 },
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    { 
      field: "sector", 
      headerName: "Sector", 
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },
    { 
      field: "routeName", 
      headerName: "RouteName", 
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },
    {
      field: "blockHours",
      headerName: "BlockHours",
      ...isMobile ? { width: 100 } : { flex: 1.5 },   
      valueFormatter: (params) => {
        if (!params) return "";
        return Number(params).toFixed(3); 
      },
    },    
    {
      field: "distance",
      headerName: "Distance (NM)",
      
    },
    {
      field: "startDate",
      headerName: "StartDate",
      ...isMobile ? { width: 100 } : { flex: 1.5 },   
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
    },
    {
      field: "endDate",
      headerName: "EndDate",
      ...isMobile ? { width: 100 } : { flex: 1.5 },   
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
    },
    { 
      field: "description", 
      headerName: "Description", 
      ...isMobile ? { width: 200 } : { flex: 5 },
    },
    { 
      field: "sectorType", 
      headerName: "SectorType", 
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(() => {
        return isMobile ? { width: 90 } : { flex: 1.2 };
      })(),   
    },
  ], [isMobile]);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,      
      sortable: false,
      width: 90,      
      renderCell: (params) => {
        return (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => {
                  if (!canEdit) return;
                  navigate(`/airportpairs/${params.row._id}`)
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
           
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => {
                  if (!canDelete) return;
                  handleDeleteClick(params.row._id);                  
                }}
              >
                <DeleteForeverIcon />
              </IconButton>                
            </Tooltip>           
          </Box>
        );
      },
    },
  ];
    
  const columnsToShow = [...columns, ...actionColumn];


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Airport Pairs" 
          subtitle={`${loading ? 'Loading...' : 'List of Airport Pairs'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Upload data" arrow>
              <span>
                <StyledButton 
                  onClick={fetchAirportPairs}
                >
                  <SyncIcon />
                </StyledButton> 
              </span>
            </Tooltip>

            <Tooltip title="Update block hours" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!canEdit) return;
                    handleUpdate();
                  }} 
                >
                  <UpdateIcon />
                </StyledButton> 
              </span>
            </Tooltip>
            
            <Tooltip title="Save" arrow>
              <span>
                <StyledButton 
                  disabled={airportPairs.length === 0}
                  onClick={() => {
                    if (!canEdit) return;
                    handleSave();
                  }}
                >
                  <SaveIcon />
                </StyledButton> 
              </span>              
            </Tooltip>
                       
            {!isMobile && (<Tooltip title="Bulk soft delete" arrow>
              <span>
                <StyledButton 
                  disabled={!isAnyRowSelected(rowSelectionModel, airportPairs)}
                  onClick={() => {
                    if (!canDelete) return;
                    handleBulkDelete();
                  }}
                >
                  <DeleteIcon />
                </StyledButton> 
              </span>
            </Tooltip>)}          
            
            <Tooltip title="Create" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!canCreate) return;
                    navigate("/airportpairs/new");
                  }}
                >
                  <AddCircleOutlineIcon />
                </StyledButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12" }}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            {isMobile ? (
              <CardList
                rows={airportPairs}
                primaryField="sector"
                fields={[
                  { label: "Route", key: "routeName" },
                  { label: "Block Hours", key: "blockHours" },
                  { label: "Distance (NM)", key: "distance" },
                  { label: "Start Date", key: "startDate" },
                  { label: "End Date", key: "endDate" },
                  { label: "Sector Type", key: "sectorType" },
                  { label: "Description", key: "description" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/airportpairs/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
            <DataGridComponent
              rows={airportPairs || []}
              columns={columnsToShow}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                processRowUpdate: handleRowUpdate,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: {
                  "& .MuiDataGrid-row.isActive-false": {
                    backgroundColor: `${theme.palette.primary.light} !important`,  
                  },
                },
              }}
            />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default AirportPairs;
